import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, HeartPulse, LogOut, Mail, Pencil, Phone, Trash2, Undo2, User, UserPlus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, ProvedorCartoesRecolhidos, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro } from '../../componentes/estados';
import { Formulario, linhasExtras, useCamposPersonalizados, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, email, nomePessoa } from '../../nucleo/formato';
import { Sensivel } from '../../componentes/sensivel';
import { useSessao } from '../../nucleo/sessao';
import { DocumentosCard, HabilidadesCard, HistoricoCard, Linha, OnboardingCard } from './FichaPartes';
import { FormFuncionario } from './FormFuncionario';
import { moduloWeb } from '../../nucleo/modulos-web';
import { ParceiroCard } from './Parceiro';
import { CartaoVerticais } from '../../componentes/verticais';
import { FormasAcesso } from '../../componentes/formas-acesso';
import { VinculosCard } from './Vinculos';
import { SeloACompletar, SeloAcesso } from './Funcionarios';
import { COR_CONTRATO, COR_ESTADO, cpfFormatado, limpar, telefoneFormatado, type Ficha } from './tipos';
import { useCamposDaTela } from '../../nucleo/campos-tela';
import { useVoltar } from '../../nucleo/voltar';


type Acao = 'afastar' | 'desligar' | 'retornar';

/** M7.1: ficha com dados, contrato, documentos, habilidades, histórico, onboarding e chatter. */
export function FichaFuncionario() {
  const { id = '' } = useParams();
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const pessoais = s.pode('pessoas.confidencial');
  const navegar = useNavigate();
  // Lista de 30/10 B (item 6): sem tela anterior no app (link colado, nova aba), a lista de funcionários.
  const voltar = useVoltar('/pessoas/pessoas/funcionarios').voltar;
  const qc = useQueryClient();
  const toast = useToast();
  const defsExtras = useCamposPersonalizados('pessoas.funcionario');
  // Lote de 19/10 (item 9): o campo escondido em Configurações > Campos das telas some da ficha (o dado continua gravado).
  const tela = useCamposDaTela('pessoas.funcionario');
  // Fase 2 (W25): depois de excluir, a ficha não pede mais o cadastro (ela segue montada até a navegação terminar).
  const [excluido, setExcluido] = useState(false);
  const q = useQuery({ queryKey: ['pessoas', 'ficha', id], queryFn: () => get<Ficha>(`/pessoas/funcionarios/${id}`), enabled: !excluido });
  const [editar, setEditar] = useState(false);
  const [acao, setAcao] = useState<Acao | null>(null);
  const [convidando, setConvidando] = useState(false);
  // Fase 2 (W25, K21): cadastro feito por engano vai para a lixeira (30 dias); quem já trabalhou é desligado (o servidor recusa).
  const [excluindo, setExcluindo] = useState(false);
  const excluir = useMutation({
    mutationFn: async () => {
      await del(`/pessoas/funcionarios/${id}`);
      setExcluido(true);
    },
    onSuccess: async () => {
      setExcluindo(false);
      toast.sucesso(tn('exclusao.funcionario.feito'));
      navegar('/pessoas/pessoas/funcionarios');
      await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
    },
    onError: (e) => {
      setExcluindo(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const retornar = useMutation({
    mutationFn: () => post(`/pessoas/funcionarios/${id}/retornar`, {}),
    onSuccess: async () => {
      setAcao(null);
      await qc.invalidateQueries({ queryKey: ['pessoas'] });
      await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
      await qc.invalidateQueries({ queryKey: ['chatter'] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  const f = q.data;
  // Fase 2 (W16): vista só pelo vínculo (cadastro de outra empresa): nada do cadastro se edita daqui.
  const soPorVinculo = !!f.so_por_vinculo;
  // Lote de 19/10: o que a importação trouxe sem campo na ficha (outro telefone, jornada sem calendário, gestor da planilha).
  const imp = (f.extras?._importacao ?? {}) as { telefone_2?: string; jornada_horas?: number; gestor?: string };
  const daImportacao = [
    ...(imp.telefone_2 ? [t('completar.telefone2', { valor: telefoneFormatado(imp.telefone_2) })] : []),
    ...(typeof imp.jornada_horas === 'number' ? [t('completar.jornada', { horas: imp.jornada_horas })] : []),
    ...(imp.gestor && !f.gestor_id ? [t('completar.gestor', { nome: nomePessoa(imp.gestor) })] : []),
  ];
  const ativo = f.estado !== 'desligado' && !soPorVinculo;
  const podeEditar = s.pode('pessoas.editar') && ativo;
  const podeExcluir = s.pode('pessoas.excluir') && !soPorVinculo;
  // Lote de 19/10 (item 16): quem não tem acesso (sem usuário e sem convite) é convidado pelo mesmo fluxo do Admin, já preenchido.
  const DialogoConvite = moduloWeb('admin')?.dialogoConvite;
  // val1 (28/10): a ficha sem e-mail também convida (por nome de usuário e senha provisória, ou ao e-mail pessoal).
  // Val5 (29/10): convidar para o sistema tem ação própria (`pessoas.convidar`).
  const podeConvidar = !!DialogoConvite && ativo && s.pode('pessoas.convidar') && !f.usuario_id && !f.convite_pendente;

  const camposEstado: DefCampo[] = [
    { nome: 'data', rotulo: t('campos.dataEfetiva'), tipo: 'data', largura: 'meia', ajuda: t('campos.dataEfetivaAjuda') },
    { nome: 'motivo', rotulo: t('campos.motivo'), tipo: 'texto', largura: 'meia' },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {(ativo || podeExcluir) && (
        <AcaoPrimaria>
          {podeExcluir && (
            <Botao icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
              {tn('exclusao.funcionario.botao')}
            </Botao>
          )}
          {/* Val5 (29/10): desligar tem ação própria (`pessoas.desligar`). */}
          {ativo && s.pode('pessoas.desligar') && (
            <Botao variante="perigo" icone={<LogOut className="size-4" />} onClick={() => setAcao('desligar')}>
              {t('acoes.desligar')}
            </Botao>
          )}
          {ativo &&
            s.pode('pessoas.editar') &&
            (f.estado === 'afastado' ? (
              <Botao icone={<Undo2 className="size-4" />} onClick={() => setAcao('retornar')}>
                {t('acoes.retornar')}
              </Botao>
            ) : (
              <Botao icone={<HeartPulse className="size-4" />} onClick={() => setAcao('afastar')}>
                {t('acoes.afastar')}
              </Botao>
            ))}
          {podeConvidar && (
            <Botao icone={<UserPlus className="size-4" />} onClick={() => setConvidando(true)}>
              {t('acoes.convidarSistema')}
            </Botao>
          )}
          {podeEditar && (
            <Botao variante="primario" icone={<Pencil className="size-4" />} onClick={() => setEditar(true)}>
              {tn('comum.editar')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <button type="button" onClick={voltar} className="flex w-fit items-center gap-1 text-sm text-tinta-3 hover:text-tinta-1">
        <ArrowLeft className="size-4" aria-hidden />
        {tn('comum.voltar')}
      </button>

      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-borda bg-sup-2 text-tinta-2">
          <User className="size-6" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-xl font-semibold" title={nomePessoa(f.nome)}>{nomePessoa(f.nome)}</h2>
          <p className="flex flex-wrap items-center gap-2 text-sm text-tinta-3">
            <span>{[tela.visivel('funcao_id') ? f.funcao_nome : null, tela.visivel('departamento_id') ? f.departamento_nome : null].filter(Boolean).join(' / ') || t('ficha.semFuncao')}</span>
            <Selo cor={COR_ESTADO[f.estado]}>{t(`estado.${f.estado}`)}</Selo>
            <Selo cor={COR_CONTRATO[f.contrato_tipo]}>{t(`contrato.${f.contrato_tipo}`)}</Selo>
            <SeloAcesso f={f} />
            <SeloACompletar f={f} />
          </p>
        </div>
      </div>

      {soPorVinculo && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('vinculos.soPorVinculo')}</p>}
      {!soPorVinculo && !!f.a_completar?.length && (
        <p className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">{t('completar.aviso', { lista: f.a_completar.map((c) => t(`completar.${c}`)).join(', ') })}</p>
      )}
      {!soPorVinculo && daImportacao.length > 0 && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('completar.daPlanilha', { lista: daImportacao.join('; ') })}</p>}
      {!soPorVinculo && f.estado === 'afastado' && (
        <p className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">{t('ficha.avisoAfastado', { data: data(f.estado_desde), motivo: f.estado_motivo ?? '-' })}</p>
      )}
      {!soPorVinculo && f.estado === 'desligado' && (
        <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('ficha.avisoDesligado', { data: data(f.desligamento), motivo: f.estado_motivo ?? '-' })}</p>
      )}

      {/* Item 10 (21/10): como na ficha de Pessoas e empresas, os cartões abrem recolhidos; a Atividade (cartão sem título) fica igual. */}
      <ProvedorCartoesRecolhidos>
      <div className="grid min-w-0 gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Cartao titulo={t('ficha.dados')}>
            <dl className="grid gap-x-8 sm:grid-cols-2">
              {/* Lote de 21/10 (item 1): CPF, nascimento e telefone só para quem tem `pessoas.confidencial`. */}
              {pessoais && (
                <Linha rotulo={t('campos.cpf')}>
                  <span className="numero">{cpfFormatado(f.cpf)}</span>
                </Linha>
              )}
              {pessoais && tela.visivel('nascimento') && (
                <Linha rotulo={t('campos.nascimento')}>{f.nascimento ? <Sensivel>{data(f.nascimento)}</Sensivel> : '-'}</Linha>
              )}
              {tela.visivel('email') && (
                <Linha rotulo={t('campos.email')}>
                  {f.email ? (
                    <a className="inline-flex items-center gap-1 hover:text-acento-forte" href={`mailto:${f.email}`}>
                      <Mail className="size-3.5" aria-hidden />
                      {email(f.email)}
                    </a>
                  ) : (
                    '-'
                  )}
                </Linha>
              )}
              {pessoais && tela.visivel('telefone') && (
                <Linha rotulo={t('campos.telefone')}>
                  {f.telefone ? (
                    <a className="numero inline-flex items-center gap-1 hover:text-acento-forte" href={`tel:${f.telefone}`}>
                      <Phone className="size-3.5" aria-hidden />
                      {telefoneFormatado(f.telefone)}
                    </a>
                  ) : (
                    '-'
                  )}
                </Linha>
              )}
              {tela.visivel('gestor_id') && (
                <Linha rotulo={t('campos.gestor')}>
                  {f.gestor_id ? (
                    <Link className="hover:text-acento-forte" to={`/pessoas/pessoas/funcionario/${f.gestor_id}`}>
                      {nomePessoa(f.gestor_nome)}
                    </Link>
                  ) : (
                    '-'
                  )}
                </Linha>
              )}
              {tela.visivel('calendario_id') && (
                <Linha rotulo={t('campos.calendario')}>{f.calendario_nome ?? '-'}</Linha>
              )}
              {/* Review r12 visual M1: rótulo da definição e valor pelo formatador único (data no idioma, texto censurado). */}
              {linhasExtras(defsExtras, f.extras, f.nome).map((l) => (
                <Linha key={l.chave} rotulo={l.rotulo}>
                  {l.valor}
                </Linha>
              ))}
            </dl>
          </Cartao>

          <Cartao titulo={t('ficha.contrato')}>
            <dl className="grid gap-x-8 sm:grid-cols-2">
              <Linha rotulo={t('campos.contrato')}>{t(`contrato.${f.contrato_tipo}`)}</Linha>
              <Linha rotulo={t('campos.admissao')}>{data(f.admissao)}</Linha>
              {tela.visivel('fim_contrato') && (
                <Linha rotulo={t('campos.fimContrato')}>{f.fim_contrato ? data(f.fim_contrato) : '-'}</Linha>
              )}
              <Linha rotulo={t('campos.salario')}>
                <span className="numero">{f.salario_centavos === null ? '-' : dinheiro(f.salario_centavos, f.moeda)}</span>
              </Linha>
              {f.experiencia && (
                <>
                  <Linha rotulo={t('ficha.experiencia45')}>{data(f.experiencia.fim45)}</Linha>
                  <Linha rotulo={t('ficha.experiencia90')}>{data(f.experiencia.fim90)}</Linha>
                </>
              )}
            </dl>
          </Cartao>

          {/* V2 verticais: em quais verticais o funcionário trabalha; com usuário ligado, são as empresas que ele acessa. */}
          {!soPorVinculo && <CartaoVerticais entidade="pessoas.funcionario" registroId={f.id} podeEditar={podeEditar} />}
          <VinculosCard ficha={f} />
          <ParceiroCard ficha={f} podeEditar={podeEditar} />
          <DocumentosCard ficha={f} podeEditar={podeEditar} />
          <HabilidadesCard ficha={f} podeEditar={podeEditar} />
          <HistoricoCard ficha={f} />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {/* Val7 (29/10, ficha FUN_formas-de-acesso): o quadro "Formas de acesso" do usuário ligado (o mesmo do Admin > Membros). */}
          {f.usuario_id && !soPorVinculo && s.pode('admin.usuarios') && f.usuario_id !== s.usuario.id && (
            <Cartao className="h-fit">
              <FormasAcesso usuarioId={f.usuario_id} desligamento={f.estado === 'desligado' ? (f.desligamento ?? f.estado_desde ?? null) : null} />
            </Cartao>
          )}
          <OnboardingCard ficha={f} podeEditar={podeEditar} />
          {/* W16: o histórico do cadastro de outra empresa não aparece para quem vê só pelo vínculo (a API responde 404). */}
          {!soPorVinculo && (
            <Cartao className="h-fit">
              <Chatter entidade="pessoas.funcionario" registroId={f.id} nomes={[f.nome]} titulo={f.nome} />
            </Cartao>
          )}
        </div>
      </div>
      </ProvedorCartoesRecolhidos>

      <FormFuncionario aberto={editar} aoFechar={() => setEditar(false)} funcionario={f} />
      {podeConvidar && DialogoConvite && <DialogoConvite aberto={convidando} aoFechar={() => setConvidando(false)} funcionario={{ id: f.id, nome: f.nome }} />}

      <Modal
        aberto={acao === 'afastar' || acao === 'desligar'}
        aoFechar={() => setAcao(null)}
        titulo={acao === 'desligar' ? t('acoes.desligar') : t('acoes.afastar')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAcao(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante={acao === 'desligar' ? 'perigo' : 'primario'} type="submit" form="form-estado">
              {tn('comum.confirmar')}
            </Botao>
          </>
        }
      >
        <p className="mb-4 text-sm text-tinta-2">{acao === 'desligar' ? t('confirmar.desligarTexto') : t('confirmar.afastarTexto')}</p>
        {acao && (
          <Formulario
            key={acao}
            id="form-estado"
            campos={camposEstado}
            inicial={{}}
            aoEnviar={async (v) => {
              await post(`/pessoas/funcionarios/${id}/${acao}`, limpar(v));
              setAcao(null);
              await qc.invalidateQueries({ queryKey: ['pessoas'] });
              await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
              await qc.invalidateQueries({ queryKey: ['chatter'] });
              toast.sucesso(tn('comum.salvo'));
            }}
          />
        )}
      </Modal>
      <Confirmar
        aberto={excluindo}
        aoFechar={() => setExcluindo(false)}
        aoConfirmar={() => excluir.mutate()}
        carregando={excluir.isPending}
        perigoso
        titulo={tn('exclusao.funcionario.titulo')}
        texto={tn('exclusao.funcionario.texto')}
      />
      <Confirmar aberto={acao === 'retornar'} aoFechar={() => setAcao(null)} aoConfirmar={() => retornar.mutate()} titulo={t('acoes.retornar')} texto={t('confirmar.retornarTexto')} carregando={retornar.isPending} />
    </div>
  );
}
